/* eslint-disable @typescript-eslint/no-explicit-any */
import { logError } from 'shared/utils/logError';
import {
  FeedbackSectionId,
  FeedbackSectionIds,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { FeedbackQuestion } from '../../../NPOFeedback/MakeEdits/api';
import { FeedbackType } from '../../../NPOFeedback/SubmitReady/utils';
import {
  FINAL_REPORT_SECTIONS,
  INTERIM_REPORT_SECTIONS,
  SECTIONS,
  SECTION_FILTER,
  getSection,
} from '../../constants';
import { labelToSectionId } from '../../../NPOFeedback/utils';
import {
  IndicatorByService,
  validateAllIndicators,
} from '../ImpactIndicator/utils';
import {
  FeedbackStatus,
  ReportStage,
} from 'donor/portal/pages/FeedbackDashboard/api';
import { Report } from 'shared/components/WelcomeBack/utils';
import { FINAL_BUDGET_QUESTION_ID } from '../../Components/Budget/utils';
import { validateModel } from '../../../NPOFeedback/MakeEdits/components/ImpactTable/modelErrors';
import {
  getFinalizeMarkerQuestionId,
  isReportInterim,
} from '../ReportFinalize/utils';
import { SectionIndicators } from '../IndicatorDetails/utils';
import { INTERIM_PROGRESS_NOTES_QUESTION_ID } from '../../navUtils';
import { ReportingType } from '../../../ReportsBeta/NewReport/api';

const isImpactSummaryCompleted = (
  lastQuestion: Answer[],
  sectionAnswers: Answer[],
) => {
  try {
    const selectedIndicators = JSON.parse(lastQuestion[0].text);
    let capacityDevelopmentEnabled: boolean | undefined = undefined;
    let actEnabled: boolean | undefined = undefined;
    let learnEnabled: boolean | undefined = undefined;
    for (const i of sectionAnswers) {
      if (i.questionId === 40021) {
        const capacityDevelopmentIndicators = i.text as string | string[];
        capacityDevelopmentEnabled = capacityDevelopmentIndicators
          ? typeof capacityDevelopmentIndicators === 'string'
            ? capacityDevelopmentIndicators.length > 2
            : capacityDevelopmentIndicators.length > 0
          : false;
      }
      if (i.questionId === 40007) {
        learnEnabled = i.text === 'Yes';
      }
      if (i.questionId === 40008) {
        actEnabled = i.text === 'Yes';
      }
    }
    if (
      capacityDevelopmentEnabled === undefined ||
      learnEnabled === undefined ||
      actEnabled === undefined
    ) {
      return false;
    }

    return validateAllIndicators(
      selectedIndicators,
      capacityDevelopmentEnabled,
      learnEnabled,
      actEnabled,
    );
  } catch (e) {
    return false;
  }
};

export interface Answer {
  dbId: number;
  text: string;
  questionId: number;
  date1: string | null;
  date2: string | null;
  autoAnswered: boolean;
  question: {
    dbId: number;
    text: string;
    section: FeedbackSectionId;
    questionType: FeedbackQuestion['questionType'];
    nextQuestionId: number | null;
    enumResponses: {
      dbId: number;
      order: number;
      responseType: string | null;
      response: string;
      responseValue: string | null;
      responseHelp: string | null;
      questionId: number;
      nextQuestionId: number;
      treeGroup: string | null;
    }[];
  };
  enumResponse: {
    dbId: number;
    response: string;
    responseType: null;
    nextQuestionId: number;
    treeGroup: null;
  } | null;
  answerValidations: any[];
}

export interface ReviewProps {
  report: Report;
  answers: Answer[];
  programName: string;
  feedbackType: FeedbackType;
  reportId: number | string;
  isReportPassed: boolean;
  isInterimStage: boolean;
  isFinalizeStage: boolean;
  reloadReport: () => unknown;
  reloadAnswers: () => unknown;
  onPreviousSection: () => unknown;
  handlePress: (stageId: 0 | 1 | 2, sectionId: number) => unknown;
}

export interface IncompleteSections {
  id: number;
  sectionName: string;
  helperText: string;
  feedbackSectionId: FeedbackSectionId;
}

export const getHelperText = (
  sectionId: FeedbackSectionId | string | number,
): string => {
  switch (sectionId) {
    case 'dates':
      return 'This ensures your report covers the correct time period for your program.';
    case 'location':
      return 'This ensures your report accurately reflects where your work is done.';
    default:
      return 'This ensures completeness of your report.';
  }
};

export type IncompleteReportObject = {
  [x in FeedbackSectionId]?: {
    sectionInfo: IncompleteSections;
    completedAnswers: Answer[];
  };
};

const finalizeIncompleteSections = (
  isCompleted: boolean,
  optionId: number,
  resultList: IncompleteSections[],
  resultObject: IncompleteReportObject,
  completedAnswers: Answer[],
  finalizeSections: { id: number; text: string }[],
) => {
  if (isCompleted) return;
  const section = finalizeSections[optionId];
  if (section) {
    const feedbackSectionId = labelToSectionId(section.text);
    if (!feedbackSectionId) return;
    const incomplete: IncompleteSections = {
      id: section.id,
      sectionName: section.text,
      feedbackSectionId: feedbackSectionId,
      helperText: getHelperText(feedbackSectionId),
    };
    resultList.push(incomplete);
    resultObject[feedbackSectionId] = {
      sectionInfo: incomplete,
      completedAnswers: completedAnswers,
    };
  }
};

const narrativeQuestionIds = [70001, 70002];
const isNarrativeSectionValid = (answers: Answer[]): boolean => {
  const narrativeAnswers = answers.filter(
    a => narrativeQuestionIds.includes(a.questionId) && Boolean(a.text),
  );
  return narrativeAnswers.length === narrativeQuestionIds.length;
};

const isProgressNotesSectionValid = (answers: Answer[]): boolean => {
  const a = answers.find(
    x => x.questionId === INTERIM_PROGRESS_NOTES_QUESTION_ID,
  );
  return Boolean(a?.text) || a?.text === '';
};

const isBudgetSectionValid = (
  answers: Answer[],
  finalBudgetAnswer: Answer | undefined,
): boolean => {
  if (finalBudgetAnswer && Boolean(finalBudgetAnswer.text)) {
    return true;
  }
  return (
    answers.filter(
      a => !a.question.nextQuestionId && !a?.enumResponse?.nextQuestionId,
    ).length === 0
  );
};

export const checkReportIsReadyToFinalize = (
  answers: Answer[],
  stage?: string | null,
) => {
  const resultList: IncompleteSections[] = [];
  const resultObject: IncompleteReportObject = {};

  const interim = isReportInterim(stage as any);
  const finalizeSections = interim
    ? INTERIM_REPORT_SECTIONS()[1].options
    : SECTIONS[2].options;
  const reportStage = stage as Report['stage'];
  const programInfoQuestionId = getFinalizeMarkerQuestionId(1, reportStage);
  const resultsQuestionId = getFinalizeMarkerQuestionId(2, reportStage);
  const beneficiariesQuestionId = getFinalizeMarkerQuestionId(3, reportStage);
  const budgetQuestionId = getFinalizeMarkerQuestionId(5, reportStage);

  let programInfo = false;
  let results = false;
  let beneficiaries = false;
  const budgetAnswers: Answer[] = [];
  const narrativeAnswers: Answer[] = [];
  const progressNotesAnswers: Answer[] = [];
  let finalBudgetAnswer: Answer | undefined = undefined;

  for (const a of answers) {
    if (a.question.section === 'narrative') {
      narrativeAnswers.push(a);
      continue;
    }
    if (a.questionId === INTERIM_PROGRESS_NOTES_QUESTION_ID) {
      progressNotesAnswers.push(a);
      continue;
    }
    if (a.question.section === 'budget') {
      budgetAnswers.push(a);
      continue;
    }
    if (a.questionId === budgetQuestionId) {
      finalBudgetAnswer = a;
      continue;
    }
    if (a.questionId === programInfoQuestionId) {
      programInfo = true;
      continue;
    }
    if (a.questionId === resultsQuestionId) {
      results = true;
      continue;
    }
    if (a.questionId === beneficiariesQuestionId) {
      beneficiaries = true;
      continue;
    }
  }

  finalizeIncompleteSections(
    programInfo,
    0,
    resultList,
    resultObject,
    [],
    finalizeSections,
  );
  finalizeIncompleteSections(
    results,
    1,
    resultList,
    resultObject,
    [],
    finalizeSections,
  );
  finalizeIncompleteSections(
    beneficiaries,
    2,
    resultList,
    resultObject,
    [],
    finalizeSections,
  );
  finalizeIncompleteSections(
    interim
      ? isProgressNotesSectionValid(progressNotesAnswers)
      : isNarrativeSectionValid(narrativeAnswers),
    3,
    resultList,
    resultObject,
    narrativeAnswers,
    finalizeSections,
  );
  finalizeIncompleteSections(
    isBudgetSectionValid(budgetAnswers, finalBudgetAnswer),
    4,
    resultList,
    resultObject,
    budgetAnswers,
    finalizeSections,
  );

  return { list: resultList, object: resultObject };
};

export const checkReportIsReady = (
  answers: Answer[],
  impactSummary: IndicatorByService,
  impactDetails: SectionIndicators,
  isReportPassed: boolean,
) => {
  const resultList: IncompleteSections[] = [];
  const resultObject: IncompleteReportObject = {};

  for (const sectionId of FeedbackSectionIds) {
    const sectionAnswers = answers.filter(
      a => a.question.section === sectionId,
    );

    let lastQuestion = sectionAnswers.filter(
      a => !a.question.nextQuestionId && !a?.enumResponse?.nextQuestionId,
    );

    if (
      sectionId === SECTION_FILTER['Impact Model Builder'] &&
      lastQuestion.length > 1
    ) {
      const q41015 = sectionAnswers.find(a => a.questionId === 40015);
      if (q41015) lastQuestion = [q41015];
    }

    let isCompleted = sectionAnswers.length > 0 && lastQuestion.length > 0;
    if (
      sectionId === SECTION_FILTER['Impact Model Builder'] &&
      isCompleted &&
      lastQuestion.length === 1
    ) {
      isCompleted = isImpactSummaryCompleted(lastQuestion, sectionAnswers);
    }

    /* Special case for impact details as now it saves a single json on submit when completed */
    if (
      sectionId === SECTION_FILTER['Impact Model Details'] &&
      sectionAnswers.length > 0
    ) {
      try {
        // const impactDetails = sectionAnswers[0].text;
        // const impactSummary = answers.find(
        //   a => a.questionId === IMPACT_SUMMARY_QUESTION_ID,
        // )?.text;
        // const summary = parseImpactSummaryAnswer(impactSummary);
        // const details = parseImpactDetailsAnswer(impactDetails);
        if (!impactSummary || !impactDetails) {
          isCompleted = false;
        } else {
          const validations = validateModel(
            impactSummary,
            impactDetails,
            isReportPassed,
          );
          isCompleted = validations.errors.length === 0;
        }
      } catch (e) {
        isCompleted = false;
      }
    }

    if (
      sectionId === SECTION_FILTER['Beneficiaries'] &&
      sectionAnswers.length > 0
    ) {
      const q60001 = sectionAnswers.find(a => a.questionId === 60001);
      if (q60001?.text) {
        try {
          const data = JSON.parse(q60001.text);
          const hasAnEmptyCategories = Object.values(data).some(
            (group: any) => group?.categories?.length === 0,
          );
          if (hasAnEmptyCategories) {
            isCompleted = false;
          }
        } catch (_e) { /* empty */ }
      }
    }

    if (!isCompleted) {
      const section = getSection(
        sectionId,
        isReportPassed ? FINAL_REPORT_SECTIONS() : undefined,
      );
      if (section) {
        const incomplete: IncompleteSections = {
          id: section.option.id,
          sectionName: section.option.text,
          feedbackSectionId: sectionId,
          helperText: getHelperText(sectionId),
        };
        resultList.push(incomplete);
        resultObject[sectionId] = {
          sectionInfo: incomplete,
          completedAnswers: sectionAnswers,
        };
      }
    }
  }
  return { list: resultList, object: resultObject };
};

const REPORT_FEEDBACK = 'report-ratings';
const getRatingSessionStorage = (): {
  [reportId: string]: boolean | undefined;
} => {
  const result = window.sessionStorage.getItem(REPORT_FEEDBACK);
  if (result) {
    try {
      const r = JSON.parse(result);
      if (typeof r === 'object') return r;
    } catch (e) {
      logError('getFeedbackSessionStorage', e as any);
    }
  }
  return {};
};

export const getRatingById = (reportId: number | string): boolean => {
  return getRatingSessionStorage()[reportId.toString()] || false;
};

export const setRatingSessionStorage = (reportId: number | string): void => {
  const current = getRatingSessionStorage();
  current[reportId.toString()] = true;
  window.sessionStorage.setItem(REPORT_FEEDBACK, JSON.stringify(current));
};

export const getReportNextStage = (
  stage: ReportStage,
  isReportEndDatePassed: boolean,
): ReportStage | undefined =>
  isReportEndDatePassed && stage?.toUpperCase() !== 'FINAL'
    ? 'FINAL'
    : undefined;

export const getReportNextStatus = (
  stage: ReportStage,
  status: FeedbackStatus,
  feedbackType: FeedbackType,
  isCNReport: boolean,
  isThirdParty: boolean,
  cnReportFeedbackEnabled: boolean,
  isReportPassed: boolean,
  totalDonations: number,
  reportingType: ReportingType,
): FeedbackStatus => {
  if (cnReportFeedbackEnabled && isCNReport && status === 'Draft') {
    return 'Awaiting TI Feedback';
  }
  if (totalDonations === 0 && status === 'Draft') {
    if (stage === 'FINAL') {
      return 'Finalized';
    }
    return 'Published';
  }
  if (status === 'Interim Updates Needed') {
    return 'Published';
  }
  if (status === 'Final Updates Needed') {
    return 'Finalized';
  }
  const isFinalizeStage =
    stage?.toUpperCase() === 'FINAL' && status !== 'Draft';
  return isFinalizeStage
    ? 'Finalized'
    : feedbackType === 'Full (TI + Donor)' || feedbackType === 'TI Only'
    ? 'Awaiting TI Feedback'
    : feedbackType === 'Donor Only'
    ? 'Awaiting Funder Feedback'
    : isReportPassed
    ? 'Finalized'
    : 'Published';
};
